import {
    Component, Input, ViewChild, ElementRef,
    AfterViewInit, OnChanges, OnDestroy, OnInit
} from '@angular/core';
import { echarts, ECharts, EChartsCoreOption } from '../echarts.config';
import type { BarSeriesOption } from 'echarts/charts';

export interface PercentageData {
    name: string;
    value: number;
}

@Component({
    selector: 'app-chart-percentage-bar',
    templateUrl: './chart-percentage-bar.component.html',
    styleUrls: ['./chart-percentage-bar.component.scss'],
    standalone: false
})
export class ChartPercentageBarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() data: PercentageData[] = [];
    @Input() maxValue: number = 100;
    @Input() height?: number;
    @Input() rowHeight: number = 40;

    @Input() title: string = '';
    @Input() subtitle: string = '';
    @Input() yAxisTitle: string = '';

    @ViewChild('container', { static: true }) container!: ElementRef;

    private chart: ECharts | null = null;
    private themeObserver!: MutationObserver;

    ngOnInit(): void {
        const html = document.documentElement;
        const body = document.body;

        if (html.classList.contains('app-dark')) {
            body.classList.add('app-dark');
        } else {
            body.classList.remove('app-dark');
        }

        this.themeObserver = new MutationObserver(() => {
            if (html.classList.contains('app-dark')) {
                body.classList.add('app-dark');
            } else {
                body.classList.remove('app-dark');
            }

            this.chart?.dispose();
            this.initChart();
        });

        this.themeObserver.observe(html, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngAfterViewInit(): void {
        this.initChart();
    }

    ngOnChanges(): void {
        if (this.chart) {
            this.updateChart();
        }
    }

    ngOnDestroy(): void {
        this.themeObserver?.disconnect();
        this.chart?.dispose();
    }

    private isDark(): boolean {
        return document.body.classList.contains('app-dark');
    }

    private theme() {
        const dark = this.isDark();
        return {
            text: dark ? '#e5e7eb' : '#374151',
            muted: dark ? '#9ca3af' : '#6b7280',
            axisLineColor: dark ? '#555' : '#ccc'
        };
    }

    private getContainerHeight(): number {
        return this.height ?? Math.max(300, this.data.length * this.rowHeight);
    }

    private getMarginLeft(): number {
        const categories = this.data.map((d) => d.name);
        const longestLabel = categories.reduce((m, s) => Math.max(m, s?.length ?? 0), 0);
        return Math.max(100, Math.min(200, longestLabel * 9));
    }

    private initChart(): void {
        this.container.nativeElement.innerHTML = '';

        // Ajuste de altura dinámica en el contenedor
        const chartHeight = this.getContainerHeight();
        this.container.nativeElement.style.height = `${chartHeight}px`;

        const theme = this.isDark() ? 'dark' : undefined;
        this.chart = echarts.init(this.container.nativeElement, theme);
        this.chart.setOption(this.buildOption());

        setTimeout(() => this.chart?.resize(), 0);
    }

    private updateChart(): void {
        if (!this.chart) return;

        // Recalcular altura y forzar resize
        const chartHeight = this.getContainerHeight();
        this.container.nativeElement.style.height = `${chartHeight}px`;

        this.chart.setOption(this.buildOption(), { replaceMerge: ['series'] });
        this.chart.resize();
    }

    private buildOption(): EChartsCoreOption {
        const t = this.theme();
        const categories = this.data.map((d) => d.name);
        const marginLeft = this.getMarginLeft();

        return {
            backgroundColor: 'transparent',
            title: {
                text: this.title,
                subtext: this.subtitle,
                left: 'center',
                textStyle: { color: t.text }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const p = Array.isArray(params) ? params[0] : params;
                    return `${p.name}<br/><b>${p.value}%</b>`;
                }
            },
            grid: {
                left: marginLeft,
                right: '12%',
                top: '25%',
                bottom: '13%',
                containLabel: false
            },
            xAxis: {
                type: 'value',
                min: 0,
                max: this.maxValue,
                name: 'Porcentaje',
                nameLocation: 'middle',
                nameGap: 30,
                axisLabel: {
                    color: t.text,
                    formatter: (val: number) => `${val}%`
                },
                axisLine: { lineStyle: { color: t.axisLineColor } },
                splitLine: { lineStyle: { color: t.axisLineColor, type: 'dashed' } }
            },
            yAxis: {
                type: 'category',
                data: categories,
                name: this.yAxisTitle || undefined,
                axisLabel: {
                    color: t.text,
                    width: marginLeft - 20,
                    overflow: 'truncate'
                },
                axisLine: { lineStyle: { color: t.axisLineColor } }
            },
            series: this.getSeries()
        };
    }

    private getSeries(): BarSeriesOption[] {
        const values = this.data.map((d) => d.value);

        return [
            {
                name: 'Porcentaje',
                type: 'bar',
                data: values,
                color: '#74a5ed',
                barMaxWidth: 30,
                label: {
                    show: true,
                    position: 'inside',
                    formatter: (params: any) => `${params.value.toFixed(1)}%`,
                    fontSize: 13,
                    color: '#fff'
                },
                emphasis: { focus: 'series' }
            }
        ];
    }
}